'use client';
import * as React from 'react';
import { useStableCallback } from '@base-ui/utils/useStableCallback';
import { useRenderElement } from '@base-ui/react/internals/useRenderElement';
import type { BaseUIComponentProps } from '@base-ui/react/internals/types';
import { useEventResizeHandler } from '../../internals/utils/useEventResizeHandler';
import { isResizeHandlerEnabled } from '../../internals/utils/resize-utils';
import { useCalendarGridDayEventContext } from '../day-event/CalendarGridDayEventContext';
import type { CalendarGridDayEvent } from '../day-event/CalendarGridDayEvent';
import type { SchedulerEventSide } from '../../models';

// Day-grid resize is native drag only; touch/pointer resize is a follow-up (add it like the time grid
// via `useEventPointerResizeHandler` with a horizontal `getDateAtPointer`). Only `enabled` is shared.
export const CalendarGridDayEventResizeHandler = React.forwardRef(
  function CalendarGridDayEventResizeHandler(
    componentProps: CalendarGridDayEventResizeHandler.Props,
    forwardedRef: React.ForwardedRef<HTMLDivElement>,
  ) {
    const {
      // Rendering props
      className,
      render,
      style,
      // Internal props
      side,
      // Props forwarded to the DOM element
      ...elementProps
    } = componentProps;

    // Context hooks
    const contextValue = useCalendarGridDayEventContext();

    // Ref hooks
    const ref = React.useRef<HTMLDivElement>(null);

    // Feature hooks
    const getDragData = useStableCallback((input) => ({
      ...contextValue.getSharedDragData(input),
      source: 'CalendarGridDayEventResizeHandler',
      side,
    }));

    const enabled = isResizeHandlerEnabled({
      side,
      doesEventStartBeforeCollectionStart: contextValue.doesEventStartBeforeCollectionStart,
      doesEventEndAfterCollectionEnd: contextValue.doesEventEndAfterCollectionEnd,
    });

    const { state } = useEventResizeHandler({
      ref,
      side,
      enabled,
      getDragData,
    });

    return useRenderElement('div', componentProps, {
      enabled,
      state,
      ref: [forwardedRef, ref],
      props: [elementProps],
    });
  },
);

export namespace CalendarGridDayEventResizeHandler {
  export interface State extends useEventResizeHandler.State {}

  export interface Props
    extends BaseUIComponentProps<'div', State>, useEventResizeHandler.PublicParameters {}

  export interface DragData extends CalendarGridDayEvent.SharedDragData {
    source: 'CalendarGridDayEventResizeHandler';
    side: SchedulerEventSide;
  }
}
